import type { CheckinResultadoDto } from 'src/app/api/generated/v3/schemas/checkinResultadoDto';
import { extractTraceId } from 'src/app/helpers/support-code.helpers';
import { formatSpDateTime, orDash } from '../../admin/shared';
import {
  dadosIngressoExpirado,
  dadosJaRealizadoParcial,
  detalheProblema,
  ehCheckinJaRealizado,
  ehIndisponivel,
  ehIngressoExpirado,
  ehSemPermissao,
  ehSessaoExpirada,
} from './checkin-erros';

/**
 * A tela de resultado de UMA leitura, como dado puro.
 *
 * Mesma decomposição do app Flutter (`ResultFullscreenDialog`): quatro tons,
 * um cabeçalho colorido, um cartão de linhas rótulo/valor e um rodapé de
 * botões. O componente só desenha o que sai daqui — o que deixa a regra de
 * "qual erro vira qual tela" testável sem câmera, sem TestBed e sem DOM.
 */

export type Tom = 'liberado' | 'usado' | 'fora' | 'ops';

export interface Linha {
  rotulo: string;
  valor: string;
  /** Nome do participante: o dado que o operador lê a um metro da tela. */
  destaque?: boolean;
}

export interface Resultado {
  tom: Tom;
  icone: string;
  titulo: string;
  subtitulo?: string;
  linhas: Linha[];
  /** Pergunta acima dos botões, como no diálogo de fora-da-janela do app. */
  prompt?: string;
  traceId?: string;
  /** Presente só quando o contrato autorizou forçar (`forceAllowed: true`). */
  codigoParaForcar?: string;
}

/**
 * Cabeçalho de cada tom, nas cores do app (`color_constants.dart`).
 *
 * Verde é SÓ a entrada aprovada. `usado` e `fora` dividem o âmbar de propósito:
 * os dois são "não passou, e olhe isto aqui" — o que os separa é o título, o
 * ícone e os botões (um só "Ler próximo" contra a escolha de forçar). O app
 * pinta `usado` de vermelho, mas aqui vermelho fica reservado para falha de
 * verdade; um ingresso repetido não é falha do sistema.
 */
export const TONS: Record<Tom, { fundo: string; texto: string; badge: string }> = {
  liberado: { fundo: '#00E35E', texto: '#292929', badge: 'rgba(41,41,41,0.12)' },
  usado: { fundo: '#FFC000', texto: '#292929', badge: 'rgba(41,41,41,0.12)' },
  fora: { fundo: '#FFC000', texto: '#292929', badge: 'rgba(41,41,41,0.12)' },
  ops: { fundo: '#292929', texto: '#FFFFFF', badge: 'rgba(255,255,255,0.10)' },
};

export function resultadoSucesso(dto: CheckinResultadoDto | null | undefined): Resultado {
  return {
    tom: 'liberado',
    icone: 'lucideCircleCheckBig',
    titulo: 'Liberado!',
    linhas: [
      { rotulo: 'Participante', valor: orDash(dto?.participante), destaque: true },
      { rotulo: 'Lote', valor: orDash(dto?.loteNome) },
      { rotulo: 'Check-in', valor: formatSpDateTime(dto?.realizadoEm ?? new Date().toISOString()) },
    ],
  };
}

/**
 * Erro → tela de resultado. A ordem é a mesma do diálogo antigo e continua
 * valendo: quem decide é `code`/`forceAllowed`, nunca o texto de `detail`.
 */
export function resultadoErro(err: unknown, codigo: string, forcar: boolean): Resultado {
  const traceId = extractTraceId(err) ?? undefined;

  if (ehCheckinJaRealizado(err)) {
    return {
      tom: 'usado',
      icone: 'lucideOctagonAlert',
      titulo: 'Usado!',
      subtitulo: 'Este ingresso já teve entrada registrada.',
      traceId,
      linhas: dadosUsado(err),
    };
  }

  // `ehIngressoExpirado` já exige `forceAllowed === true`: a permissão de
  // forçar vem do contrato, não da tela. Depois de forçar não se oferece
  // forçar de novo — senão o operador entra num laço.
  if (!forcar && ehIngressoExpirado(err)) {
    const d = dadosIngressoExpirado(err);
    return {
      tom: 'fora',
      icone: 'lucideClock',
      titulo: 'Fora do horário!',
      subtitulo: 'A janela de check-in deste lote já foi encerrada.',
      prompt: 'Fazer o check-in mesmo assim?',
      codigoParaForcar: codigo,
      traceId,
      linhas: [
        { rotulo: 'Participante', valor: d.nomeParticipante, destaque: true },
        { rotulo: 'Lote', valor: d.nomeLote },
        { rotulo: 'Período de check-in do lote', valor: d.periodo },
      ],
    };
  }

  return {
    tom: 'ops',
    icone: 'lucideCircleX',
    titulo: 'Ops!',
    subtitulo: mensagem(err),
    traceId,
    linhas: [],
  };
}

/**
 * Quem é o portador, quem registrou e quando — a informação que resolve a
 * discussão na porta, e a razão de este 409 ter tela própria em vez de virar
 * um "código já utilizado".
 *
 * `dadosJaRealizadoParcial` degrada campo a campo de propósito: um corpo
 * incompleto mostra `--` no que faltou em vez de derrubar o cartão inteiro.
 */
function dadosUsado(err: unknown): Linha[] {
  const d = dadosJaRealizadoParcial(err);
  return [
    { rotulo: 'Participante', valor: d.nome, destaque: true },
    ...(d.cpf === '--' ? [] : [{ rotulo: 'CPF', valor: d.cpf }]),
    { rotulo: 'Lote', valor: d.nomeLote },
    { rotulo: 'Check-in realizado', valor: d.dataCheckin },
    { rotulo: 'Registrado por', valor: d.operador },
  ];
}

function mensagem(err: unknown): string {
  if (ehSemPermissao(err)) {
    return detalheProblema(
      err,
      'Sua permissão neste evento não inclui registrar check-in. Fale com quem administra o evento.',
    );
  }
  if (ehSessaoExpirada(err)) {
    return 'Sessão expirada. Entre novamente para continuar registrando check-ins.';
  }
  if (ehIndisponivel(err)) {
    // A escrita já repetiu com a MESMA Idempotency-Key antes de chegar aqui: o
    // servidor pode ter gravado e perdido a resposta.
    return 'Sem resposta do servidor. O check-in pode ter sido registrado — confira na lista antes de tentar de novo.';
  }
  return detalheProblema(err, 'Código não encontrado ou já utilizado.');
}
